import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Menu, Bell, LogOut, Play, Pause, Clock, Lock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useMarketAutomation } from '../contexts/MarketAutomationContext'
import { useWebSocket } from '../contexts/WebSocketContext'
import { useThrottledTicks } from '../hooks/useThrottledTicks'
import { quotes } from '../data/motivationalQuotes'
import { streamingAPI } from '../services/api'

// ── Actual NIFTY 50 + BANKNIFTY — subscribed as AMXIDX tokens ────────────
// Keys match AngelOne master `symbol` field (toUpperCase): 'NIFTY 50', 'NIFTY BANK'.
// Displayed as static chips (not in the scrolling marquee).
// FAIL CLOSED: IndexChip renders null when ltp is falsy (stream not yet running).
const INDEX_SYMBOLS = [
  { symbol: 'NIFTY 50',   label: 'NIFTY 50'  },  // AMXIDX token 99926000
  { symbol: 'NIFTY BANK', label: 'BANKNIFTY' },  // AMXIDX token 99926009
]

// ── ETF ticker symbols (scrolling marquee) ───────────────────────────────
// All symbols verified against the cached AngelOne master contract
// (.angelone-master.json). Symbols the streamer can't resolve to a token
// will silently show '—' (FAIL CLOSED — never display bad data).
// AMXIDX index tokens are subscribed SEPARATELY via INDEX_SYMBOLS above.
const TICKER_SYMBOLS = [
  // ── Broad market ETFs ────────────────────────────────────────────────
  { symbol: 'NIFTYBEES',  label: 'NIFTY 50'    },  // Nippon India Nifty BeES
  { symbol: 'BANKBEES',   label: 'BANKNIFTY'   },  // Nippon India Bank BeES
  { symbol: 'JUNIORBEES', label: 'NEXT 50'     },  // Nippon India Junior BeES
  { symbol: 'MID150BEES', label: 'MIDCAP 150'  },  // Nippon India Midcap 150
  { symbol: 'MOSMALL250', label: 'SMALLCAP'    },  // MO Nifty Smallcap 250
  // ── Sector ETFs ──────────────────────────────────────────────────────
  { symbol: 'ITBEES',     label: 'IT'          },  // Nippon India IT BeES
  { symbol: 'PHARMABEES', label: 'PHARMA'      },  // Nippon India Pharma BeES
  { symbol: 'AUTOBEES',   label: 'AUTO'        },  // Nippon India Auto BeES
  { symbol: 'FMCGIETF',   label: 'FMCG'       },  // ICICI Pru FMCG ETF
  { symbol: 'METALIETF',  label: 'METAL'       },  // ICICI Pru Metal ETF
  { symbol: 'CONSUMBEES', label: 'CONSUMPTION' },  // Nippon India Consumption
  { symbol: 'PSUBNKBEES', label: 'PSU BANK'   },  // Nippon India PSU Bank
  // ── Thematic / Government ETFs ───────────────────────────────────────
  { symbol: 'CPSEETF',    label: 'CPSE'        },  // Nippon India CPSE ETF
  { symbol: 'ICICIB22',   label: 'BHARAT 22'   },  // ICICI Pru Bharat 22 ETF
  { symbol: 'LOWVOL',     label: 'LOW VOL'     },  // Nippon India Low Volatility
  // ── Commodities ──────────────────────────────────────────────────────
  { symbol: 'GOLDBEES',   label: 'GOLD'        },  // Nippon India Gold BeES
  { symbol: 'SILVERBEES', label: 'SILVER'      },  // Nippon India Silver BeES
  // ── Liquid ───────────────────────────────────────────────────────────
  { symbol: 'LIQUIDBEES', label: 'LIQUID'      },  // Nippon India Liquid BeES
]

// ── Pure helpers ──────────────────────────────────────────────────────────

function pickQuote() {
  return quotes[Math.floor(Math.random() * quotes.length)]
}

// Build ticker rows from the live AngelOne tick snapshot.
// The backend broadcasts: { ltp, change (absolute), close (prev-day close), ... }
// There is no changePercent field — we derive it from change / close × 100.
// Returns '—' for any symbol not yet in the snapshot.
function buildTickerFromTicks(ticks) {
  return TICKER_SYMBOLS.map(({ symbol, label }) => {
    const t = ticks?.[symbol]
    if (!t) return { symbol, label, ltp: null, change: null, changePct: null }

    const ltp    = t.ltp    ?? null
    const change = t.change ?? null
    const close  = t.close  ?? null   // previous-day close sent in every AngelOne tick

    // Derive % change; guard against zero close (avoid ÷0)
    const changePct = (change != null && close != null && close !== 0)
      ? parseFloat(((change / close) * 100).toFixed(2))
      : null

    return { symbol, label, ltp, change, changePct }
  })
}

// Returns true when at least one symbol has live price data.
function hasLiveData(ticker) {
  return ticker.some(t => t.ltp != null)
}

function estimateContentLength(quote, ticker) {
  const quoteLen  = (quote.text?.length ?? 0) + (quote.author?.length ?? 0) + 30
  const tickerLen = ticker.length * 38
  return quoteLen + tickerLen + 60
}

function animDuration(len) {
  return Math.max(60, Math.ceil(len / 7))
}

// ── Module-level components (stable references — never remount on tick) ───

const TickerItem = ({ label, ltp, change, changePct }) => {
  const isUp  = change != null && change >= 0
  const color = change == null
    ? 'text-gray-400'
    : isUp ? 'text-success-600' : 'text-danger-600'

  return (
    <span className="inline-flex items-baseline gap-1 mx-5 select-none">
      <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-sm font-bold ${color}`}>
        {ltp != null ? ltp.toFixed(2) : '—'}
      </span>
      {change != null && (
        <span className={`text-xs font-semibold ${color}`}>
          {isUp ? '▲' : '▼'}&nbsp;{Math.abs(change).toFixed(2)}
          {changePct != null && (
            <span className="opacity-75">&nbsp;({isUp ? '+' : ''}{changePct.toFixed(2)}%)</span>
          )}
        </span>
      )}
    </span>
  )
}

// ── IndexChip — static live index display ────────────────────────────────
// Shows actual NIFTY 50 / BANKNIFTY values from AngelOne SmartStream AMXIDX ticks.
// ltp IS the index value directly (binary tick ÷100) — NO multiplier.
// FAIL CLOSED: returns null when ltp is falsy (stream not connected / no tick yet).
function IndexChip({ label, ltp, change, close }) {
  if (!ltp) return null
  const pct  = (close > 0 && change != null) ? ((change / close) * 100).toFixed(2) : null
  const isUp = change == null || change >= 0
  return (
    <div
      className="hidden sm:flex flex-col items-end leading-none select-none shrink-0"
      title={`${label} — live index via AngelOne SmartStream`}
    >
      <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-xs font-bold tabular-nums ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
        {ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      {pct !== null && (
        <span className={`text-[9px] tabular-nums ${isUp ? 'text-emerald-500' : 'text-red-400'}`}>
          {isUp ? '▲' : '▼'}&nbsp;{Math.abs(Number(pct))}%
        </span>
      )}
    </div>
  )
}

// ── Header ────────────────────────────────────────────────────────────────
const Header = ({ onMenuClick, connected }) => {
  const { logout, user } = useAuth()
  const { fallbackActive } = useWebSocket()
  const {
    isAutomationActive,
    startAutomation,
    stopAutomation,
    lastWatchlistUpdate,
    isUpdatingWatchlist,
    isGeneratingSignals,
  } = useMarketAutomation()

  // Live AngelOne ticks (throttled to 5 s — avoids constant re-renders)
  const symbolTicks = useThrottledTicks(5000)

  // Always-current ref — read at cycle-end to snapshot without triggering renders
  const latestTicksRef = useRef(symbolTicks)
  latestTicksRef.current = symbolTicks

  // ── Marquee container measurement ────────────────────────────────────
  // We measure the actual container pixel width and pass it as --ticker-from
  // so the animation starts exactly at the container's right edge.
  // Without this, 100vw overshoots by the width of the fixed side panels,
  // causing a blank-header delay of several seconds before text appears.
  const containerRef    = useRef(null)
  const [containerWidth, setContainerWidth] = useState(1200) // safe first-paint fallback

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    setContainerWidth(el.clientWidth)
    const ro = new ResizeObserver(([entry]) => setContainerWidth(Math.round(entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ── Hover-to-pause (container-level — more reliable than :hover on a moving element) ──
  const [isPaused, setIsPaused] = useState(false)

  // ── AngelOne streaming status ─────────────────────────────────────────
  // Independent of the browser WS connection.
  // streamRunning = AngelOne backend stream is active + connected.
  const [streamRunning, setStreamRunning]   = useState(false)
  const [streamStarting, setStreamStarting] = useState(false)

  // Fetch true streaming status from backend. Runs on mount and every 30 s.
  useEffect(() => {
    const check = async () => {
      try {
        const res = await streamingAPI.getStatus()
        setStreamRunning(res.data?.running === true && res.data?.connected === true)
      } catch {
        setStreamRunning(false)
      }
    }
    check()
    const iv = setInterval(check, 30_000)
    return () => clearInterval(iv)
  }, [])

  // Subscribe ticker ETFs whenever the stream comes up.
  // streamingAPI.subscribe is additive on the backend — does NOT
  // overwrite Portfolio's subscriptions in WebSocketContext.
  useEffect(() => {
    if (!streamRunning) return
    // Subscribe ETF ticker symbols + AMXIDX index tokens in one call.
    // The backend sends AMXIDX tokens in a separate subscription message to
    // isolate any index subscription failure from the equity batch.
    const syms = [
      ...TICKER_SYMBOLS.map(s => s.symbol),
      ...INDEX_SYMBOLS.map(s => s.symbol),
    ]
    streamingAPI.subscribe(syms).catch(() => {})
  }, [streamRunning])

  // Start the AngelOne stream and then subscribe ticker + index symbols.
  const handleStartStream = useCallback(async () => {
    setStreamStarting(true)
    try {
      await streamingAPI.start()
      setStreamRunning(true)
      const syms = [
        ...TICKER_SYMBOLS.map(s => s.symbol),
        ...INDEX_SYMBOLS.map(s => s.symbol),
      ]
      streamingAPI.subscribe(syms).catch(() => {})
    } catch {
      // ignore — user can retry via Streaming page
    } finally {
      setStreamStarting(false)
    }
  }, [])

  // ── Cycle state machine ───────────────────────────────────────────────
  const [snap, setSnap] = useState(() => ({
    quote:  pickQuote(),
    ticker: buildTickerFromTicks({}),
  }))
  const [animKey,      setAnimKey]      = useState(0)
  const [snapHasData,  setSnapHasData]  = useState(false)

  // ── Warm-up: inject first real tick data without waiting a full cycle ──
  // Fires every 5 s when symbolTicks updates (via useThrottledTicks).
  // As soon as any ticker symbol has a live price, we:
  //   1. Rebuild the ticker rows with real prices
  //   2. Bump animKey → span remounts → animation restarts from the right
  //   3. Set snapHasData = true so this never fires again
  useEffect(() => {
    if (snapHasData) return   // already have real data, nothing to do
    const ticks  = latestTicksRef.current
    const ticker = buildTickerFromTicks(ticks)
    if (!hasLiveData(ticker)) return   // still no prices — wait
    setSnap(prev => ({ ...prev, ticker }))
    setAnimKey(k => k + 1)
    setSnapHasData(true)
  }, [symbolTicks, snapHasData])  // re-checks each 5 s throttle cycle

  // Called when animation span completes one full traversal.
  // Freezes latest tick data into the next snapshot.
  const handleCycleEnd = useCallback(() => {
    const newSnap = {
      quote:  pickQuote(),
      ticker: buildTickerFromTicks(latestTicksRef.current),
    }
    setSnap(newSnap)
    setAnimKey(k => k + 1)
    if (!snapHasData && hasLiveData(newSnap.ticker)) setSnapHasData(true)
  }, [snapHasData])

  const duration = useMemo(
    () => animDuration(estimateContentLength(snap.quote, snap.ticker)),
    [snap]
  )

  return (
    <header className="bg-white border-b border-gray-200 h-16 flex items-center gap-3 px-4 overflow-hidden">
      {/* Mobile sidebar toggle */}
      <button
        onClick={onMenuClick}
        className="lg:hidden shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* ── Left: LIVE badge OR Start Stream button ──────────────────────
          Reflects true AngelOne streaming state (not just WS connection).   */}
      {streamRunning ? (
        // Stream is active — show green LIVE indicator
        <div className="shrink-0 hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-success-200 bg-success-50">
          <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
          <span className="text-xs font-bold tracking-widest text-success-600 select-none">LIVE</span>
        </div>
      ) : (
        // Stream not running — show Start Stream button
        <button
          onClick={handleStartStream}
          disabled={streamStarting}
          className="shrink-0 hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-primary-200 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-60"
        >
          <Play className={`w-2.5 h-2.5 text-primary-600 ${streamStarting ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-bold text-primary-600 tracking-wide">
            {streamStarting ? 'Starting…' : 'Start Stream'}
          </span>
        </button>
      )}

      {/* ── Continuous marquee: Quote ——— Ticker ────────────────────────── */}
      {/* onMouseEnter/Leave on the container (stationary) is reliable;
          :hover on a translating span can miss events at boundary crossings. */}
      <div
        ref={containerRef}
        className="flex-1 min-w-0 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        title="Hover to pause"
      >
        <span
          key={animKey}
          className="inline-block whitespace-nowrap select-none cursor-default"
          style={{
            animationName:           'marqueeScroll',
            animationDuration:       `${duration}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: '1',
            animationFillMode:       'forwards',
            animationPlayState:      isPaused ? 'paused' : 'running',
            '--ticker-from':         `${containerWidth}px`,
          }}
          onAnimationEnd={handleCycleEnd}
        >
          {/* ── Quote ─────────────────────────────────────────────────── */}
          <span className="text-sm font-medium text-gray-800">{snap.quote.text}</span>
          <span className="text-sm font-semibold text-primary-600 ml-2">
            &mdash;&nbsp;{snap.quote.author}
          </span>

          {/* ── Gap between quote and ticker ─────────────────────────── */}
          <span className="mx-12 text-gray-200 text-base" aria-hidden="true">|</span>

          {/* ── ETF / Index ticker items (20 symbols) ────────────────── */}
          {snap.ticker.map(item => <TickerItem key={item.symbol} {...item} />)}

          {/* Trailing gap: ensures last item fully clears viewport before cycle resets */}
          <span className="inline-block w-40" aria-hidden="true" />
        </span>
      </div>

      {/* ── Right side ──────────────────────────────────────────────────── */}
      <div className="flex items-center space-x-4">
        {/* Market Automation */}
        {user?.kiteConnected && (
          <div className="flex items-center space-x-3">
            <button
              onClick={isAutomationActive ? stopAutomation : startAutomation}
              className={`flex items-center space-x-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                isAutomationActive
                  ? 'bg-success-100 text-success-700 hover:bg-success-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={isAutomationActive ? 'Stop automation' : 'Start automation'}
            >
              {isAutomationActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span className="hidden sm:inline">
                {isAutomationActive ? 'Auto' : 'Manual'}
              </span>
            </button>

            {isAutomationActive && (
              <div className="flex items-center space-x-2 text-sm">
                {(isUpdatingWatchlist || isGeneratingSignals) ? (
                  <div className="flex items-center space-x-1 text-primary-600">
                    <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse" />
                    <span className="hidden sm:inline">
                      {isUpdatingWatchlist ? 'Updating...' : 'Generating...'}
                    </span>
                  </div>
                ) : lastWatchlistUpdate ? (
                  <div className="flex items-center space-x-1 text-gray-600">
                    <Clock className="w-3 h-3" />
                    <span className="hidden sm:inline">{lastWatchlistUpdate.toLocaleTimeString()}</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* ── Live index values — actual NIFTY 50 + BANKNIFTY from AngelOne AMXIDX ──
            FAIL CLOSED: IndexChip renders null when ltp is falsy.
            No multiplier — ltp from binary tick IS the index value directly.  */}
        <IndexChip
          label="NIFTY 50"
          ltp={symbolTicks['NIFTY 50']?.ltp}
          change={symbolTicks['NIFTY 50']?.change}
          close={symbolTicks['NIFTY 50']?.close}
        />
        <IndexChip
          label="BANKNIFTY"
          ltp={symbolTicks['NIFTY BANK']?.ltp}
          change={symbolTicks['NIFTY BANK']?.change}
          close={symbolTicks['NIFTY BANK']?.close}
        />

        {/* ── Discipline reminder — replaces the "Live/API/Offline" status text ──
            The LIVE badge (left) already covers streaming status.
            This is a permanent commitment reminder, always amber.              */}
        <div className="hidden sm:flex items-center gap-1.5 select-none" title="Investment discipline reminder">
          <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <span className="text-xs font-semibold text-amber-600">
            Cap: &#8377;63L &mdash; No emotional top-ups
          </span>
        </div>

        {/* Notifications */}
        <button className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 relative">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-danger-500 rounded-full"></span>
        </button>

        {/* Logout */}
        <button
          onClick={logout}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    </header>
  )
}

export default Header
